import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, Settings2, Lock, Unlock, Save,
  Sunrise, Sunset, Sun, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const TRIGGER_META = {
  booking_checkout:  { label: "When a booking ends",          color: "bg-amber-50 text-amber-800 border-amber-200" },
  booking_arrival:   { label: "Before a booking arrives",     color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  lease_move_out:    { label: "When a lease ends",            color: "bg-rose-50 text-rose-800 border-rose-200" },
  lease_move_in:     { label: "Before a lease starts",        color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  weekly_recurring:  { label: "Every week",                   color: "bg-purple-50 text-purple-800 border-purple-200" },
};

const SLOT_META = {
  morning:   { label: "Morning",   Icon: Sunrise },
  afternoon: { label: "Afternoon", Icon: Sunset },
  all_day:   { label: "All day",   Icon: Sun },
};

const DOW = [
  { k: 0, label: "Mon" }, { k: 1, label: "Tue" }, { k: 2, label: "Wed" },
  { k: 3, label: "Thu" }, { k: 4, label: "Fri" }, { k: 5, label: "Sat" }, { k: 6, label: "Sun" },
];

const blankRule = () => ({
  id: null,
  name: "",
  trigger_type: "booking_checkout",
  task_title: "",
  task_description: "",
  slot: "all_day",
  priority: "normal",
  offset_days: 0,
  weekday: 0,
  applies_to_unit_ids: [],
  active: true,
  is_built_in: false,
});

export default function HelperTaskRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, uRes] = await Promise.all([
        api.get("/housing/helper-task-rules"),
        api.get("/housing/units"),
      ]);
      setRules(rRes.data);
      setUnits(uRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = {};
    rules.forEach((r) => {
      if (!out[r.trigger_type]) out[r.trigger_type] = [];
      out[r.trigger_type].push(r);
    });
    return out;
  }, [rules]);

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Rule name is required"); return; }
    if (!draft.task_title.trim()) { toast.error("Task title is required"); return; }
    if (draft.trigger_type === "weekly_recurring" && (draft.weekday == null)) {
      toast.error("Pick a weekday for the weekly rule"); return;
    }
    setSaving(true);
    const body = {
      name: draft.name.trim(),
      trigger_type: draft.trigger_type,
      task_title: draft.task_title.trim(),
      task_description: draft.task_description || "",
      slot: draft.slot || "all_day",
      priority: draft.priority || "normal",
      offset_days: parseInt(draft.offset_days) || 0,
      weekday: draft.trigger_type === "weekly_recurring" ? parseInt(draft.weekday) : null,
      applies_to_unit_ids: draft.applies_to_unit_ids || [],
      active: !!draft.active,
    };
    try {
      if (draft.id) {
        await api.put(`/housing/helper-task-rules/${draft.id}`, body);
        toast.success("Rule updated");
      } else {
        await api.post("/housing/helper-task-rules", body);
        toast.success("Rule created");
      }
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/housing/helper-task-rules/${rule.id}`, { ...rule, active: !rule.active });
      toast.success(rule.active ? "Rule paused" : "Rule activated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (rule) => {
    if (rule.is_built_in) { toast.error("Built-in rules can be paused but not deleted"); return; }
    if (!window.confirm(`Delete "${rule.name}"? Existing auto-generated tasks from this rule will also be removed.`)) return;
    try {
      await api.delete(`/housing/helper-task-rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/housing/helper-tasks/regenerate");
      toast.success("Auto-tasks rebuilt for the next 60 days");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setRegenerating(false); }
  };

  const SlotIcon = SLOT_META[draft?.slot || "all_day"]?.Icon || Sun;

  return (
    <div className="space-y-6 fade-in" data-testid="helper-rules-page">
      <Button variant="ghost" onClick={() => navigate("/helper-tasks")} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to tasks
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Settings2 className="h-8 w-8 text-purple-700" /> Helper task auto-rules
          </h1>
          <p className="text-[#525C58] mt-1">Tell the system when to create helper tasks automatically — when a booking ends, before a lease starts, every Monday, etc.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={regenerate} disabled={regenerating} data-testid="regen-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Rebuilding…" : "Rebuild auto-tasks"}
          </Button>
          <Button onClick={() => setDraft(blankRule())} className="bg-purple-700 hover:bg-purple-800" data-testid="new-rule-btn">
            <Plus className="h-4 w-4 mr-1" /> New custom rule
          </Button>
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader><CardTitle className="text-base">Rules ({rules.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-center py-10 text-[#9AA39E]" data-testid="loading">Loading…</p>
          ) : rules.length === 0 ? (
            <p className="text-center py-10 text-[#9AA39E]">No rules defined yet.</p>
          ) : (
            <div className="divide-y divide-[#E8E8E6]">
              {Object.entries(TRIGGER_META).map(([trig, meta]) => {
                const list = grouped[trig] || [];
                if (!list.length) return null;
                return (
                  <div key={trig} className="px-6 py-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-[#525C58] mb-2 flex items-center gap-2">
                      <Badge className={`${meta.color} border text-[10px]`}>{meta.label}</Badge>
                      <span className="text-[#9AA39E] font-normal">{list.length} rule{list.length !== 1 ? "s" : ""}</span>
                    </h3>
                    <div className="space-y-2">
                      {list.map((r) => {
                        const slot = SLOT_META[r.slot || "all_day"];
                        const SIcon = slot?.Icon || Sun;
                        return (
                          <div key={r.id} className={`p-3 rounded-lg border flex items-start justify-between gap-3 hover:bg-[#F4F4F1] transition ${r.active ? "bg-white border-[#E8E8E6]" : "bg-slate-50 border-slate-200 opacity-70"}`} data-testid={`rule-row-${r.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-[#1B4332]">{r.name}</span>
                                {r.is_built_in ? (
                                  <Badge variant="outline" className="text-[10px]"><Lock className="h-3 w-3 mr-1" /> Built-in</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200"><Unlock className="h-3 w-3 mr-1" /> Custom</Badge>
                                )}
                                {!r.active && <Badge className="bg-slate-200 text-slate-700 border-0 text-[10px]">Paused</Badge>}
                                <Badge variant="outline" className="text-[10px]"><SIcon className="h-3 w-3 mr-1" /> {slot?.label}</Badge>
                                {r.trigger_type === "weekly_recurring" && r.weekday != null && (
                                  <Badge variant="outline" className="text-[10px]">{DOW[r.weekday]?.label}</Badge>
                                )}
                                {r.offset_days !== 0 && (
                                  <Badge variant="outline" className="text-[10px] text-[#7A8A84]">
                                    {r.offset_days < 0 ? `${Math.abs(r.offset_days)}d before` : `${r.offset_days}d after`}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-[#525C58] mt-1 truncate">→ <span className="font-medium">{r.task_title}</span></p>
                              {r.task_description && <p className="text-[11px] text-[#9AA39E] mt-0.5 line-clamp-2">{r.task_description}</p>}
                            </div>
                            <div className="flex gap-1 shrink-0 items-center">
                              <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`toggle-${r.id}`} />
                              <Button size="sm" variant="ghost" onClick={() => setDraft({ ...r })} className="text-[#1B4332] h-7 w-7 p-0" data-testid={`edit-${r.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {!r.is_built_in && (
                                <Button size="sm" variant="ghost" onClick={() => del(r)} className="text-red-600 h-7 w-7 p-0" data-testid={`delete-${r.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {draft?.id ? `Edit rule: ${draft.name}` : "New auto-rule"}
              {draft?.is_built_in && <Badge variant="outline" className="ml-2 text-[10px]"><Lock className="h-3 w-3 mr-1" /> Built-in</Badge>}
            </DialogTitle>
            <DialogDescription>
              Each rule turns a real-world event into a helper task. Use placeholders like {"{unit_name}"}, {"{visitor}"} or {"{tenant}"} in the title and description — they're filled in when the task is created.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1 md:col-span-2">
                  <Label>Rule name *</Label>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Booking checkout clean" data-testid="rule-name" />
                </div>
                <div className="space-y-1">
                  <Label>Trigger</Label>
                  <Select value={draft.trigger_type} onValueChange={(v) => setDraft({ ...draft, trigger_type: v })} disabled={draft.is_built_in}>
                    <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {draft.is_built_in && <p className="text-[10px] text-[#9AA39E]">Built-in rules' trigger is fixed.</p>}
                </div>
                <div className="space-y-1">
                  <Label>Slot</Label>
                  <Select value={draft.slot} onValueChange={(v) => setDraft({ ...draft, slot: v })}>
                    <SelectTrigger data-testid="rule-slot"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SLOT_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Task title *</Label>
                  <Input value={draft.task_title} onChange={(e) => setDraft({ ...draft, task_title: e.target.value })} placeholder="e.g. Checkout clean — {unit_name}" data-testid="rule-task-title" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Task description</Label>
                  <Textarea rows={2} value={draft.task_description} onChange={(e) => setDraft({ ...draft, task_description: e.target.value })} data-testid="rule-task-description" />
                </div>
                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                    <SelectTrigger data-testid="rule-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {draft.trigger_type !== "weekly_recurring" ? (
                  <div className="space-y-1">
                    <Label>Offset days</Label>
                    <Input type="number" value={draft.offset_days} onChange={(e) => setDraft({ ...draft, offset_days: e.target.value })} data-testid="rule-offset" />
                    <p className="text-[10px] text-[#9AA39E]">Negative = before the event (e.g. -1 = the day before)</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label>Weekday</Label>
                    <Select value={String(draft.weekday)} onValueChange={(v) => setDraft({ ...draft, weekday: parseInt(v) })}>
                      <SelectTrigger data-testid="rule-weekday"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOW.map((d) => <SelectItem key={d.k} value={String(d.k)}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1 md:col-span-2 flex items-center justify-between p-2 rounded border border-[#E8E8E6]">
                  <Label className="text-sm flex items-center gap-2">
                    <SlotIcon className="h-4 w-4" />
                    Active
                  </Label>
                  <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} data-testid="rule-active" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-purple-700 hover:bg-purple-800" data-testid="save-rule-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : (draft?.id ? "Save changes" : "Create rule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
